import { useEffect, useState } from "react";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { formatCurrency, formatNumberWithDecimal } from "../../../utils/commonUtil";
import { REIButton } from "../../../components/ui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

interface PropertyData {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  currentMarketEstimate: number;
  netEquity: number;
  lvr: string;
  dsr: string;
  preTaxCashflow: number;
  capitalGrowth: number;
  capitalGrowthPercentage: number;
  propertyId: number;
}

const MyPropertyAtGlance: React.FC<{ entityId: string }> = ({ entityId }) => {
  const trackerData = useSelector((state: RootState) => state.myTracker.trackerData);
  const [transformedData, setTransformedData] = useState<PropertyData[]>([]);
  const [propertiesTotal, setPropertiesTotal] = useState<any>({});
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  useEffect(() => {
    if (trackerData) {
      const entityData =
        entityId === "0"
          ? trackerData.find((item) => item.EntityId === "")
          : trackerData.find((item) => item.EntityId === entityId);
      if (entityData == undefined) {
        setTransformedData([]);
        setPropertiesTotal({});
        return;
      }
      if (entityData) {
        setTransformedData(transformData(entityData.Properties || []));
        setPropertiesTotal(
          isShowingTotals
            ? entityData.PropertyTotal.PropertyKeyValuesTotal || {}
            : entityData.PropertyTotal.PropertyKeyValuesPortion || {}
        );
      }
    }
  }, [trackerData, entityId, isShowingTotals]);

  const transformData = (data: any[]): PropertyData[] => {
    let groupCounter: number = -1;
    let lastGroupKey: string = "";
    return data.flatMap((property) =>
      ["Forecast", "Actuals", "Variance"].map((type) => {
        const groupKey = property.StreetAddress;
        if (groupKey !== lastGroupKey) {
          groupCounter++;
          lastGroupKey = groupKey;
        }

        return {
          street: property.StreetAddress,
          suburb: property.Suburb,
          state: property.State,
          postcode: property.Postcode,
          country: property.Country,
          type,
          currentMarketEstimate: isShowingTotals
            ? Number(property.PropertyKeyValuesTotal[type]?.CurrentMarketValue) || 0
            : Number(property.PropertyKeyValuesPortion[type]?.CurrentMarketValue) || 0,
          netEquity: isShowingTotals
            ? Number(property.PropertyKeyValuesTotal[type]?.NetEquity) || 0
            : Number(property.PropertyKeyValuesPortion[type]?.NetEquity) || 0,
          lvr: formatNumberWithDecimal(
            isShowingTotals
              ? Number(property.PropertyKeyValuesTotal[type]?.LVR) || 0
              : Number(property.PropertyKeyValuesPortion[type]?.LVR) || 0,
            0,
            2
          ),
          dsr: formatNumberWithDecimal(
            isShowingTotals
              ? Number(property.PropertyKeyValuesTotal[type]?.DSR) || 0
              : Number(property.PropertyKeyValuesPortion[type]?.DSR) || 0,
            0,
            2
          ),
          preTaxCashflow: isShowingTotals
            ? Number(property.PropertyKeyValuesTotal[type]?.PreTaxCashflow) || 0
            : Number(property.PropertyKeyValuesPortion[type]?.PreTaxCashflow) || 0,
          capitalGrowth: isShowingTotals
            ? Number(property.PropertyKeyValuesTotal[type]?.CapitalGrowth) || 0
            : Number(property.PropertyKeyValuesPortion[type]?.CapitalGrowth) || 0,
          capitalGrowthPercentage: Number(property.PropertyKeyValuesPortion[type]?.CapitalGrowthPercentage) || 0,
          propertyId: property.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        };
      })
    );
  };

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatNumberWithDecimal(-value, 2, 2)}%)` : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const renderFooter = (category: string) => (
    <Row>
      <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
      <Column field="type" footer={category} />
      <Column
        footer={currencyTemplate(propertiesTotal?.[category]?.CurrentMarketValue)}
        footerStyle={{ textAlign: "right" }}
      />
      <Column footer={currencyTemplate(propertiesTotal?.[category]?.NetEquity)} footerStyle={{ textAlign: "right" }} />
      <Column
        footer={
          propertiesTotal?.[category]?.LVR < 0 ? (
            <span className="text-red-600">({formatNumberWithDecimal(-propertiesTotal?.[category]?.LVR, 2, 2)}%)</span>
          ) : (
            `${formatNumberWithDecimal(propertiesTotal?.[category]?.LVR, 2, 2)}%`
          )
        }
        footerStyle={{ textAlign: "right" }}
      />
      <Column
        footer={
          propertiesTotal?.[category]?.DSR < 0 ? (
            <span className="text-red-600">({formatNumberWithDecimal(-propertiesTotal?.[category]?.DSR, 2, 2)})</span>
          ) : (
            `${formatNumberWithDecimal(propertiesTotal?.[category]?.DSR, 2, 2)}`
          )
        }
        footerStyle={{ textAlign: "right" }}
      />
      <Column
        footer={currencyTemplate(propertiesTotal?.[category]?.PreTaxCashflow)}
        footerStyle={{ textAlign: "right" }}
      />
      <Column
        footer={currencyTemplate(propertiesTotal?.[category]?.CapitalGrowth)}
        footerStyle={{ textAlign: "right" }}
      />
      <Column
        footer={percentageTemplate(propertiesTotal?.[category]?.CapitalGrowthPercentage)}
        footerStyle={{ textAlign: "right" }}
      />
    </Row>
  );

  const footerGroup = (
    <ColumnGroup>
      {renderFooter("Forecast")}
      {renderFooter("Actuals")}
      {renderFooter("Variance")}
    </ColumnGroup>
  );

  return (
    trackerData && (
      <REIToggablePanel
        header=""
        istoggleable={false}
        defaultCollapse={false}
        customHeader={(options: any) => (
          <div className={`${options.className} justify-between flex bg-slate-600 text-white`}>
            <span className="font-bold">My properties at a glance – current view</span>
            <REIButton
              id="mtpag-btn-addproperty"
              label="Add a new property"
              classNames="p-2 mr-1"
              onClick={() => {
                navigate(`/portfolio-tracker`, {
                  state: {
                    tab: "MyPropertyTab",
                    MyPropertyId: undefined,
                    AddNewProperty: true,
                    EditProperty: false,
                  },
                });
              }}
            />
          </div>
        )}
        contentComponent={
          transformedData.length > 0 ? (
            <DataTable
              value={transformedData}
              rowGroupMode="rowspan"
              groupRowsBy="street"
              footerColumnGroup={footerGroup}
              rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "groupRowWhite")}
            >
              <Column
                field="street"
                header="Address/property name"
                body={(rowData) => (
                  <a
                    id={`mtpag-lnk-address-${rowData.propertyId}`}
                    data-testid={`mtpag-lnk-address-${rowData.propertyId}`}
                    className="cursor-pointer text-blue-700 no-underline"
                    style={{ textDecoration: "none" }}
                    onClick={() => {
                      navigate(`/portfolio-tracker`, {
                        state: {
                          tab: "MyPropertyTab",
                          MyPropertyId: rowData.propertyId,
                        },
                      });
                    }}
                  >
                    {`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}
                  </a>
                )}
              />
              <Column field="type" header="Type" />
              <Column
                field="currentMarketEstimate"
                header="Current Market Estimate"
                body={(rowData) => currencyTemplate(rowData.currentMarketEstimate)}
                align="right"
              />
              <Column
                field="netEquity"
                header="Net Equity"
                body={(rowData) => currencyTemplate(rowData.netEquity)}
                align="right"
              />
              <Column field="lvr" header="LVR" body={(rowData) => `${rowData.lvr}%`} align="right" />
              <Column
                field="dsr"
                header="DSR"
                body={(rowData) => (
                  <span className={rowData.dsr < 0 ? "text-red-600" : ""}>
                    {rowData.dsr < 0
                      ? `(${formatNumberWithDecimal(-rowData.dsr, 2, 2)})`
                      : `${formatNumberWithDecimal(rowData.dsr, 2, 2)}`}
                  </span>
                )}
                align="right"
              />
              <Column
                field="preTaxCashflow"
                header="Pre-tax Cash Flow"
                body={(rowData) => currencyTemplate(rowData.preTaxCashflow)}
                align="right"
              />
              <Column
                field="capitalGrowth"
                header="$ Cap Growth"
                body={(rowData) => currencyTemplate(rowData.capitalGrowth)}
                align="right"
              />
              <Column
                field="capitalGrowthPercentage"
                header="% Cap Growth"
                body={(rowData) => percentageTemplate(rowData.capitalGrowthPercentage)}
                align="right"
              />
            </DataTable>
          ) : (
            <div className="flex flex-col p-3 content-center items-center">
              Add your investment properties to Portfolio Tracker to start measuring not only their cashflow and equity,
              but also what you thought would happen vs what actually is happening.
            </div>
          )
        }
      />
    )
  );
};

export default MyPropertyAtGlance;
